<template>
  <div class="phone" aria-hidden="true">
    <!-- Decorative hero illustration: a coded miniature of the real chat
         screen, not a screenshot. Nothing here is interactive, and the whole
         subtree is hidden from assistive tech because the surrounding hero
         copy already says everything this picture says. The comment lives
         inside the root element on purpose: a sibling comment would make the
         component a fragment and drop the root attributes. -->
    <div class="phone-screen">
      <div class="phone-head">
        <img src="@/assets/articulearn-logo-a.png" class="phone-head-logo" alt="" />
        <span class="phone-head-titles">
          <span class="phone-head-title">{{ $t("home.mockup.title") }}</span>
          <span class="phone-head-subtitle">{{ $t("home.mockup.subtitle") }}</span>
        </span>
      </div>
      <div class="phone-body">
        <div class="turn-tutor">
          <span class="tutor-id">
            <img src="@/assets/articulearn-logo-a.png" class="tutor-id-logo" alt="" />
            <span class="tutor-id-name">Arti</span>
          </span>
          <p class="tutor-text">{{ $t("home.mockup.artiGreeting") }}</p>
          <span class="audio-pill">
            <span class="audio-play"></span>
            <span class="audio-wave"><span v-for="n in 7" :key="n" class="bar"></span></span>
            <span class="audio-time">0:07</span>
          </span>
          <span class="spoken-chip">{{ $t("home.mockup.showSpoken") }}</span>
        </div>
        <div class="turn-student">
          <div class="student-bubble">
            {{ $t("home.mockup.studentReply") }}
            <span class="audio-pill audio-pill-student">
              <span class="audio-play"></span>
              <span class="audio-wave"><span v-for="n in 5" :key="n" class="bar"></span></span>
              <span class="audio-time">0:04</span>
            </span>
          </div>
        </div>
        <div class="turn-tutor">
          <p class="tutor-text">{{ $t("home.mockup.artiCorrection") }}</p>
        </div>
      </div>
      <div class="phone-mic">
        <span class="mic-button">
          <svg viewBox="0 0 24 24" focusable="false">
            <path
              d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z"
            />
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default { name: "PhoneChatMockup" };
</script>

<style scoped>
/* Marketing routes are light-mode only (the pre-paint stamp in
   public/index.html gates dark on /dashboard/ and native), so this component
   deliberately has no dark-mode branch. Three of the tokens below
   (--al-warm-soft, --al-shadow-sm, --al-shadow-md) ARE remapped under
   :root[data-theme="dark"]; they stay because fidelity to the real chat UI is
   the point of this illustration and dark never reaches this route.
   The bezel/screen radii are device geometry with no token equivalent, and the
   18/18/6/18 bubble radii are copied verbatim from StudentMessage.vue. */
.phone {
  width: 250px;
  height: 500px;
  border-radius: 38px;
  padding: 11px;
  background: var(--al-ink-900);
  box-shadow: var(--al-shadow-md);
  position: relative;
  flex-shrink: 0;
}

/* Notch. Sits over the screen's top edge, inside the bezel padding. */
.phone::before {
  content: "";
  position: absolute;
  top: 11px;
  left: 50%;
  transform: translateX(-50%);
  width: 84px;
  height: 22px;
  background: var(--al-ink-900);
  border-radius: 0 0 14px 14px;
  z-index: 3;
}

.phone-screen {
  background: var(--al-white);
  border-radius: 28px;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.phone-head {
  background: var(--al-ink-900);
  color: var(--al-bg-light);
  padding: 26px 14px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* White chip behind the mark, matching TutorMessage's identity logo: the
   brand mark is hue-fixed and needs a light plate on the ink header. */
.phone-head-logo {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--al-white);
  object-fit: contain;
  flex-shrink: 0;
}

/* min-width: 0 lets the titles shrink instead of pushing the logo out of the
   250px screen when a locale's header copy is long. */
.phone-head-titles {
  min-width: 0;
}

.phone-head-title,
.phone-head-subtitle {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.phone-head-title {
  font-size: 12.5px;
  font-weight: 600;
}

.phone-head-subtitle {
  font-size: 9.5px;
  color: var(--al-gray-400);
}

/* Three declarations here are load-bearing for i18n. The phone is a fixed
   500px column and the longest locales run ~25% past English (de artiGreeting
   is 123 chars vs 99 in en-us), so the conversation does not always fit.
   - min-height: 0 + overflow: hidden stop flex's automatic minimum size from
     pushing the mic bar past the screen's clip boundary, which would delete it.
   - justify-content: flex-end then decides WHICH end clips. Anchored to the
     bottom, an over-long translation loses the opening of the greeting off the
     top, exactly like a real chat scrolled to its newest message. Anchored to
     the top (the default) it would instead cut off Arti's correction, which is
     the one turn the illustration exists to show. */
.phone-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
}

/* Arti speaks as open-canvas text with an identity row (per TutorMessage.vue);
   the student speaks in the warm bubble (per StudentMessage.vue). */
.turn-tutor {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tutor-id {
  display: flex;
  align-items: center;
  gap: 5px;
}

.tutor-id-logo {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  object-fit: contain;
}

.tutor-id-name {
  font-size: 9.5px;
  font-weight: 700;
  color: var(--al-gray-700);
}

/* 20px indent = 15px logo + 5px gap, so the flow aligns under the name. */
.tutor-text {
  font-size: 11px;
  line-height: 1.5;
  color: var(--al-gray-700);
  padding-left: 20px;
  margin: 0;
  overflow-wrap: anywhere;
}

.audio-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--al-bg-light);
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-pill);
  padding: 4px 9px 4px 4px;
  margin: 6px 0 0 20px;
  width: fit-content;
}

.audio-play {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--al-primary);
  position: relative;
  flex-shrink: 0;
}

.audio-play::after {
  content: "";
  position: absolute;
  left: 6px;
  top: 4px;
  border-left: 6px solid var(--al-white);
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
}

.audio-wave {
  display: flex;
  gap: 2px;
  align-items: center;
  height: 12px;
}

/* Unplayed fill copied from AudioPlayer.vue's .bar, which mixes the muted text
   role 45% toward transparent. --al-gray is that role's light-mode value, used
   directly because this route never renders dark. Bars 1-3 flip to primary to
   read as played, the same played/unplayed split the real player draws. */
.audio-wave .bar {
  width: 2.5px;
  background: color-mix(in srgb, var(--al-gray) 45%, transparent);
  border-radius: 2px;
  display: block;
}

.audio-wave .bar:nth-child(-n + 3) {
  background: var(--al-primary);
}

.audio-wave .bar:nth-child(1) {
  height: 5px;
}

.audio-wave .bar:nth-child(2) {
  height: 9px;
}

.audio-wave .bar:nth-child(3) {
  height: 6px;
}

.audio-wave .bar:nth-child(4) {
  height: 11px;
}

.audio-wave .bar:nth-child(5) {
  height: 5px;
}

.audio-wave .bar:nth-child(6) {
  height: 8px;
}

.audio-wave .bar:nth-child(7) {
  height: 4px;
}

.audio-time {
  font-size: 8px;
  color: var(--al-gray);
}

/* Bordered pill, matching TutorMessage.vue's .spoken-chip. A <span>, not the
   real component's <button>: nothing under the aria-hidden root is focusable. */
.spoken-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  font-size: 8.5px;
  font-weight: 600;
  color: var(--al-gray);
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-pill);
  padding: 3px 8px;
  margin: 2px 0 0 20px;
}

/* Chevron drawn from borders rather than a glyph: the loaded webfonts have no
   arrowhead codepoint, so a text chevron would fall back or render as tofu. */
.spoken-chip::before {
  content: "";
  width: 4px;
  height: 4px;
  border-right: 1px solid var(--al-gray);
  border-bottom: 1px solid var(--al-gray);
  transform: translateY(-1px) rotate(45deg);
  flex-shrink: 0;
}

.turn-student {
  display: flex;
  justify-content: flex-end;
}

/* 18/18/6/18 radii and the warm fill, matching StudentMessage.vue. */
.student-bubble {
  background: var(--al-warm-soft);
  color: var(--al-ink-900);
  border-radius: 18px 18px 6px 18px;
  padding: 8px 11px;
  font-size: 11px;
  line-height: 1.5;
  max-width: 82%;
  box-shadow: var(--al-shadow-sm);
  overflow-wrap: anywhere;
}

/* `display: flex` (not inline-flex) mirrors StudentMessage.vue: inline-flex
   would park the pill beside the transcript and stretch the bubble, so the
   real component drops it onto its own line. The inherited `width: fit-content`
   from .audio-pill keeps it compact rather than bubble-wide.
   Two classes deliberately: the prod CSS minifier can reorder equal-specificity
   rules, so the student override wins on specificity rather than on order. */
.audio-pill.audio-pill-student {
  display: flex;
  background: var(--al-white);
  margin-left: 0;
}

.phone-mic {
  padding: 10px 12px 16px;
  display: flex;
  justify-content: center;
  border-top: 1px solid var(--al-surface-muted);
}

/* Glow geometry copied from the real record button (AudioRecorder.vue), but
   the tint comes off --al-primary via color-mix instead of that file's raw
   rgba, so no new color value enters the system. */
.mic-button {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--al-primary);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--al-primary) 35%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
}

.mic-button svg {
  width: 20px;
  height: 20px;
  fill: var(--al-white);
}
</style>
